﻿import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from'moment';

import RawHtml from '../../../UI/RawHtml';
import ClauseInfo from '../../../UI/ClauseInfo';
import UpgradeCta from './UpgradeCta';
import AnimalUpgradeCta from './AnimalUpgradeCta';
import {ComparisonWrapper, LinkButton} from '../../../UI/pageComponents';

import {getPremiumLabel} from '../../../DataHelpers/lfDataHelpers';

import definitions from '../../../Style/LfStyleDefinitions';
import {mediaQuery, clearFix} from '../../../Style/cssHelpers';

class PossessionInfo extends React.Component {
    static get propTypes() {
        return {
            possession: PropTypes.object.isRequired
        }
    }

    constructor(props) {
        super(props);
        this.insuranceRawHtmlInfoEl = null;
    }

    componentDidMount() {
        if (this.insuranceRawHtmlInfoEl) { // should we correctly select the current insurance possession level (basic, medium, large, etc.)?
            this.fadeOutInsuranceComparisonLevels(this.insuranceRawHtmlInfoEl); 
        }
    }

    getInsuranceWebMeta(name) {
        if (!(name in this.props.data.insurancesByName)) {
            return null; 
        }

        const groupName = this.props.data.insurancesByName[name].groupName;
        if (groupName in this.props.data.metaData) {
            return this.props.data.metaData[groupName];
        }

        return null; 
    }

    // fade out available insurance upgrades in possession insurance comparison
    // while keeping the possession insurance opaque

    fadeOutInsuranceComparisonLevels(parentEl) {
        const fadeOutOpacity = '0.5';

        let comparisonEls = parentEl.querySelectorAll('*[data-lf-comparison-level]');
        let comparisonElsToFade = [...comparisonEls] // nodelist to array
            .filter(el => 
                el.getAttribute('data-lf-comparison-level') 
                    .split(',').map(s => s.trim()) // may contain comma-separated ids: "home-basic, home-basic-cooperative, home-basic-cooperative-security"
                    .indexOf(this.props.possession.insuranceName) == -1
            );

        comparisonElsToFade.forEach(el => {
            el.style.opacity = fadeOutOpacity;
            if (typeof el.firstElementChild !== 'undefined' && el.firstElementChild) { //ie-fix
                el.firstElementChild.style.opacity = fadeOutOpacity; 
            }
        });
    }

    render() {

        const animalInsuranceGroups = [
            'cat',
            'cat-life',
            'dog',
            'dog-life',
            'small-animal'
        ];

        const possession = this.props.possession;
        const posGroupName = possession.insuranceName in this.props.data.insurancesByName
            ? this.props.data.insurancesByName[possession.insuranceName].groupName
            : '';
        const metaData = this.getInsuranceWebMeta(possession.insuranceName);
        const insuranceData = possession.insuranceName in this.props.data.insurancesByName 
            ? this.props.data.insurancesByName[possession.insuranceName] 
            : null; 

        const isCoinsured = insuranceData && insuranceData.isCoinsured;

        const member = this.props.data.memberInfo;
        const name = `${member.firstName} ${member.lastName}`;
        const coinsuredName = possession.coInsuredName
            ? possession.coInsuredName
            : '-'; 

        const estimatedInsuranceEndDate = !possession.estimatedInsuranceEndDate 
            ? 'Tills vidare' 
            : moment(possession.estimatedInsuranceEndDate).format('YYYY-MM-DD');
            
        const fromDate = moment(possession.fromDate).format('YYYY-MM-DD');
        const toDate = !possession.toDate 
            ? estimatedInsuranceEndDate 
            : moment(possession.toDate).format('YYYY-MM-DD');

        const ordinaryPremiumFromDate = possession.ordinaryPremiumFromDate 
            ? moment(possession.ordinaryPremiumFromDate).format('YYYY-MM-DD') 
            : null;

        const isOffer = possession.isOffer;
        const isAnimalInsurance = animalInsuranceGroups.indexOf(posGroupName) > -1;

        const hasRawHtmlInfo = metaData && metaData.insuranceInfoHtml != null;

        const hasUpgrades = possession.insuranceName in this.props.data.insurancesByName 
            && this.props.data.insurancesByName[possession.insuranceName].possibleUpgrades.length > 0 
            && !possession.isNonUpgradeable;

        const areUpgradesAvailable = hasUpgrades && (() => {
            const possibleUpgrades = this.props.data.insurancesByName[possession.insuranceName].possibleUpgrades;
            const quotesByName = this.props.data.quotesByName;
            const availableUpgrades = possibleUpgrades.filter((upgrade) => {
                return !!this.props.data.quotesByName[upgrade];
            });

            return !!availableUpgrades.length;
        })();

        const hasSingleClause = (typeof possession.clauseText !== 'undefined' && possession.clauseText != null);
        const hasMultiClause = (typeof possession.clauses !== 'undefined' && possession.clauses.length > 0);
        const hasClauses = hasSingleClause || hasMultiClause;

        return (
            <div>
                {hasRawHtmlInfo && 
                    <ComparisonWrapper className="rounded">
                        <RawHtml inputRef={el => { this.insuranceRawHtmlInfoEl = el }}>
                            {metaData.insuranceInfoHtml}
                        </RawHtml>
                    </ComparisonWrapper>
                }

                {hasUpgrades && areUpgradesAvailable && !isAnimalInsurance &&
                    <UpgradeCta possession={possession} alignment={hasRawHtmlInfo ? 'center' : 'left'} />
                }

                {hasUpgrades && areUpgradesAvailable && isAnimalInsurance &&
                    <AnimalUpgradeCta possession={possession} alignment={hasRawHtmlInfo ? 'center' : 'left'} />
                }

                <Wrapper>
                    <Content>
                        <Label>Försäkringstagare:</Label>
                        <Value>{isCoinsured ? coinsuredName : name}</Value>
                        <Label>Gäller från:</Label>
                        <Value>{fromDate}</Value>
                        <Label>Gäller till:</Label>
                        <Value>{toDate}</Value>

                        <Label>{getPremiumLabel(possession.premiumPeriod)}:</Label>
                        <Value>{possession.premium}kr</Value>

                        {possession.ordinaryPremium != null &&
                            <React.Fragment>
                                <Label>Ordinarie pris:</Label>
                                <Value>{possession.ordinaryPremium ? possession.ordinaryPremium : '- '}kr</Value>
                                <Label>Ordinarie pris från:</Label>
                                <Value>{ordinaryPremiumFromDate}</Value>
                            </React.Fragment>
                        }

                        {isOffer &&
                            <React.Fragment>
                                <Label>Erbjudande:</Label>
                                <Value>Ja</Value>
                            </React.Fragment>
                        }
                    </Content>
                </Wrapper>

                {hasClauses &&
                        <Wrapper>
                            <ClauseInfo heading="Klausuler" possession={possession} />
                        </Wrapper>
                }

                {metaData && 
                    <Wrapper textAlign="center">
                        <LinkButton className="rounded" href={metaData.url}>Läs mer om försäkringens innehåll</LinkButton>
                    </Wrapper>
                }
            </div>
        );   
    }
}

// state

const mapStateToProps = (state) => {
    return {
        data: state.startPageData
    };
};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PossessionInfo);

// styles

const Wrapper = styled.div`
    display: table;
    width: 100%; 
    margin-bottom: 17px;
    margin-top: 35px;
        
    text-align: ${props => props.textAlign ? props.textAlign : 'left'};

    ${mediaQuery.medium(`
        &:nth-of-type(2) {
            margin-bottom: 10px;
        }
    `)}

    ${mediaQuery.small(`
        margin-top: 20px;
    `)}

    ${mediaQuery.xsmall(`
        max-width: none;
    `)}

    ${mediaQuery.xxsmall(`
        max-width: 88vw;
    `)}
`;

const Content = styled.div`
    width: 100%;
    
    padding: 0 50px 0 50px;
    margin: 0px;

    font-family: ${definitions.fonts.main};
    font-size: 18px;

    ${mediaQuery.medium(`
        padding: 0px 20px;
    `)}

    ${mediaQuery.small(`
        width: 100%; 
        padding: 0;
        line-height: 1.4;
        font-size: 16px;
        margin: 17px 0px 0px 0px;
        &:nth-of-type(2) {
            margin: 0px;
        }
    `)}

    color: white;

    ${clearFix()}
`;
const Column = styled.div`
    float: left;
    width: 50%;
    
    padding: 0 50px 0 50px;
    margin: 0px;

    font-family: ${definitions.fonts.main};
    font-size: 18px;

    ${mediaQuery.medium(`
        padding: 0px 20px;
    `)}

    ${mediaQuery.small(`
        width: 100%; 
        padding: 0;
        line-height: 1.4;
        font-size: 16px;
        margin: 17px 0px 0px 0px;
        &:nth-of-type(2) {
            margin: 0px;
        }
    `)}

    color: white;
    
    &:nth-of-type(odd) {
        border-right: 1px solid rgba(255, 255, 255, 0.25); 

        ${mediaQuery.xsmall(`
            border-right: none;
        `)}

        ${mediaQuery.small(`
            border-right: none;
        `)}

        ${mediaQuery.medium(`
            border-right: none;
        `)}
    }

    ${clearFix()}
`;

const Label = styled.div`
    width: 50%; 
    float: left; 
    ${mediaQuery.xsmall(`
        width: 100%; 
        float: none;
    `)}

    text-align: left; 
    font-weight: bold;
`;

const Value = styled.div`
    width: 50%; 
    float: left; 

    ${mediaQuery.small(`
        margin-bottom: 10px;
    `)}

    ${mediaQuery.xsmall(`
        width: 100%; 
        float: none;
    `)}

    text-align: right; 
    ${mediaQuery.xsmall(`
        text-align: left; 
    `)}

    font-weight: normal;
`;